import React, { useState, useEffect, useRef } from 'react';

export default function QRScannerModern({ onScan, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setIsScanning(true);
      setError('');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        scanFrame();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(
        err.name === 'NotAllowedError' 
          ? 'Camera access denied. Please allow camera permissions to scan QR codes.'
          : err.name === 'NotFoundError'
          ? 'No camera found on this device.'
          : 'Failed to access camera. Please check permissions and try again.'
      );
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  const scanFrame = async () => {
    if (!isScanning || !videoRef.current) return;

    try {
      // Use BarcodeDetector API if available (modern browsers)
      if ('BarcodeDetector' in window) {
        const barcodeDetector = new BarcodeDetector({ formats: ['qr_code'] });
        const barcodes = await barcodeDetector.detect(videoRef.current);
        
        if (barcodes.length > 0) {
          onScan(barcodes[0].rawValue);
          stopCamera();
          return;
        }
      } else {
        // Fallback: manual QR detection would go here
        // For now, we'll show an error and stop scanning
        setError('QR code scanning not supported in this browser. Please try Chrome or Edge.');
        stopCamera();
        return;
      }
      
      // Continue scanning
      requestAnimationFrame(scanFrame);
    } catch (err) {
      console.error('Scanning error:', err);
      if (isScanning) {
        requestAnimationFrame(scanFrame);
      }
    }
  };

  const handleRetry = () => {
    stopCamera();
    startCamera();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Scan QR Code</h2>
            <button 
              onClick={() => {
                stopCamera();
                onClose();
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
            {isScanning && (
              <video
                ref={videoRef}
                className="w-full h-64 object-cover"
                playsInline
                muted
              />
            )}
            
            {!isScanning && !error && (
              <div className="w-full h-64 flex items-center justify-center bg-gray-200">
                <div className="text-gray-500">Loading camera...</div>
              </div>
            )}

            {error && (
              <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 p-4 text-center">
                <div className="text-red-500 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-red-600 text-sm mb-3">{error}</p>
                <button
                  onClick={handleRetry}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Try Again
                </button>
              </div>
            )}

            {isScanning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="border-2 border-green-500 rounded-lg w-48 h-48 animate-pulse"></div>
              </div>
            )}
          </div>

          <div className="text-center text-sm text-gray-600 mb-4">
            {isScanning && !error && (
              <p>Point your camera at a QR code to scan</p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                stopCamera();
                onClose();
              }}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md transition"
            >
              Cancel
            </button>
            {error && (
              <button
                onClick={handleRetry}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
